import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Description,
  Disclosure,
  DisclosureGroup,
  FieldError,
  Input,
  Label,
  ListBox,
  Select,
  TextField,
} from "@heroui/react";
import { api, AppUser, MealSlot, NutritionProfile, nutritionProfileOf, Role, SlotException } from "../api/client";
import { SlotDefaultsEditor } from "../components/SlotDefaultsEditor";
import { SlotExceptionsEditor } from "../components/SlotExceptionsEditor";
import { NutritionProfileEditor } from "../components/NutritionProfileEditor";
import { useAuth } from "../auth";

export function AdminUsers() {
  const { user: me, refresh } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);

  async function load() {
    const data = await api.get<AppUser[]>("/users");
    setUsers(data);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSaved(updated: AppUser) {
    setUsers((arr) => arr.map((u) => (u.id === updated.id ? updated : u)));
    if (me && me.id === updated.id) await refresh();
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-4">
        <h1 className="text-2xl font-bold">Gestió d&apos;usuaris</h1>
        <p className="text-sm text-muted">
          Com a administrador pots editar qualsevol usuari de la família.
        </p>
      </header>
      <DisclosureGroup className="flex flex-col gap-3">
        {users.map((u) => (
          <UserItem key={u.id} user={u} onSaved={handleSaved} />
        ))}
      </DisclosureGroup>
    </div>
  );
}

const ROLES: { value: Role; label: string }[] = [
  { value: "admin", label: "Administrador" },
  { value: "member", label: "Membre" },
];

interface UserItemProps {
  user: AppUser;
  onSaved: (u: AppUser) => Promise<void>;
}

function UserItem({ user, onSaved }: UserItemProps) {
  return (
    <Card>
      <Disclosure>
        <Disclosure.Heading>
          <Disclosure.Trigger className="w-full">
            <div className="flex w-full items-center gap-3 p-3 text-left">
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl"
                style={{ background: user.avatarColor }}
                aria-hidden
              >
                {user.avatarEmoji}
              </span>
              <div className="flex-1">
                <div className="text-base font-semibold">{user.name}</div>
                <div className="text-xs text-muted">{user.email}</div>
              </div>
              <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-xs">
                {user.role === "admin" ? "Admin" : "Membre"}
              </span>
            </div>
          </Disclosure.Trigger>
        </Disclosure.Heading>
        <Disclosure.Content>
          <div className="border-t border-default-100 p-4">
            <UserForm user={user} onSaved={onSaved} />
          </div>
        </Disclosure.Content>
      </Disclosure>
    </Card>
  );
}

function UserForm({ user, onSaved }: UserItemProps) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<Role>(user.role);
  const [emoji, setEmoji] = useState(user.avatarEmoji);
  const [color, setColor] = useState(user.avatarColor);
  const [prefs, setPrefs] = useState(stringifyOrEmpty(user.preferences));
  const [intol, setIntol] = useState(stringifyOrEmpty(user.intolerances));
  const [slotDefaults, setSlotDefaults] = useState<MealSlot[]>(user.slotDefaults ?? []);
  const [slotExceptions, setSlotExceptions] = useState<SlotException[]>(user.slotExceptions ?? []);
  const [nutritionProfile, setNutritionProfile] = useState<NutritionProfile>(nutritionProfileOf(user));
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const passwordInvalid = password.length > 0 && password.length < 6;

  async function handleSave() {
    if (passwordInvalid) return;
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      const payload: Record<string, unknown> = {
        name,
        email,
        role,
        avatarEmoji: emoji,
        avatarColor: color,
        preferences: prefs,
        intolerances: intol,
        slotDefaults,
        slotExceptions,
        ...nutritionProfile,
      };
      if (password) payload.password = password;
      const updated = await api.patch<AppUser>(`/users/${user.id}`, payload);
      setPassword("");
      await onSaved(updated);
      setOk(true);
    } catch (e: any) {
      setError(e.message ?? "Error en desar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-3">
        <TextField className="flex-1 min-w-[160px]" value={name} onChange={setName}>
          <Label>Nom</Label>
          <Input />
        </TextField>
        <TextField className="flex-1 min-w-[200px]" type="email" value={email} onChange={setEmail}>
          <Label>Email</Label>
          <Input />
        </TextField>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Select
          className="min-w-[180px]"
          selectedKey={role}
          onSelectionChange={(k) => setRole(k as Role)}
        >
          <Label>Rol</Label>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {ROLES.map((r) => (
                <ListBox.Item key={r.value} id={r.value} textValue={r.label}>
                  {r.label}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
        <TextField className="w-20" value={emoji} onChange={setEmoji}>
          <Label>Emoji</Label>
          <Input className="text-center" />
        </TextField>
        <div className="flex flex-col gap-1">
          <span className="text-sm">Color</span>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-10 w-16 cursor-pointer rounded border border-default-200 bg-transparent"
            aria-label="Color avatar"
          />
        </div>
      </div>

      <TextField value={prefs} onChange={setPrefs}>
        <Label>Preferències</Label>
        <Input />
      </TextField>
      <TextField value={intol} onChange={setIntol}>
        <Label>Intoleràncies / al·lèrgies</Label>
        <Input />
      </TextField>

      <SlotDefaultsEditor value={slotDefaults} onChange={setSlotDefaults} />

      <SlotExceptionsEditor
        slotDefaults={slotDefaults}
        value={slotExceptions}
        onChange={setSlotExceptions}
      />

      <NutritionProfileEditor value={nutritionProfile} onChange={setNutritionProfile} />

      <TextField
        isInvalid={passwordInvalid}
        type="password"
        value={password}
        onChange={setPassword}
      >
        <Label>Reset de contrasenya</Label>
        <Input placeholder="Deixa-ho buit per no canviar-la" />
        {passwordInvalid ? (
          <FieldError>Mínim 6 caràcters</FieldError>
        ) : (
          <Description>Opcional. Mínim 6 caràcters.</Description>
        )}
      </TextField>

      <div className="flex items-center gap-3">
        <Button isPending={busy} onPress={handleSave}>
          Desa
        </Button>
        {ok && <span className="text-sm text-success">Canvis desats.</span>}
        {error && <span className="text-sm text-danger">{error}</span>}
      </div>
    </div>
  );
}

function stringifyOrEmpty(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}
